import seoIcon from '@/assets/2hq.svg'
import agentIcon from '@/assets/ai-query-icon.svg'
import databaseIcon from '@/assets/datasets.svg'
import folderIcon from '@/assets/documents.svg'
import helpIcon from '@/assets/Help.svg'
import homeIcon from '@/assets/home.svg'
import settingsIcon from '@/assets/Settings.svg'
import supportIcon from '@/assets/support.svg'
import { BookOpen, Briefcase, Building2, Image, Map, Mic, Sparkles, Target, Users } from 'lucide-react'

export type NavigationVisibility = 'core' | 'role' | 'parked'

export function isAdvancedNavigationUser(
    user?: {
        isAdmin?: boolean
        email?: string
        role?: { name?: string }
    } | null
): boolean {
    if (!user) return false
    if (user.isAdmin || user.email?.toLowerCase().endsWith('@500rockets.io')) return true
    return ['admin', 'owner', 'strategist', 'power', 'power_user', 'creator', 'builder'].includes(
        user.role?.name?.toLowerCase() ?? ''
    )
}

export const menuItems = [
    // ── WORKSPACE ─────────────────────────────────────────────────────────────
    {
        name: 'Studio',
        link: `/dashboard/campaigns/utilities/studio`,
        icon: Sparkles,
        isAdminRoute: false,
        match: ['campaigns/utilities/studio'],
        id_element: 'sidebar-deliverable-studio',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
    },
    {
        name: 'Home',
        link: `/dashboard/home`,
        icon: homeIcon,
        isAdminRoute: false,
        match: ['home'],
        id_element: 'sidebar-home',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
    {
        name: 'Subjects',
        link: `/dashboard/subjects`,
        icon: Users,
        isAdminRoute: false,
        match: ['subjects'],
        id_element: 'sidebar-subjects',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
    {
        name: 'Documents',
        link: `/dashboard/documents`,
        icon: folderIcon,
        isAdminRoute: false,
        match: ['documents', 'document'],
        id_element: 'sidebar-documents',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
    {
        name: 'Knowledge Review',
        link: `/dashboard/knowledge-review`,
        icon: BookOpen,
        isAdminRoute: false,
        match: ['knowledge-review'],
        id_element: 'sidebar-knowledge-review',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
    {
        name: 'Interviews',
        link: `/dashboard/interview`,
        icon: Mic,
        isAdminRoute: false,
        match: ['interview', 'interviews'],
        id_element: 'sidebar-interviews',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
    {
        name: 'Opportunities',
        link: `/dashboard/events`,
        icon: Target,
        isAdminRoute: false,
        match: ['events'],
        id_element: 'sidebar-opportunities',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
    {
        name: 'Materials',
        link: `/dashboard/materials`,
        icon: Briefcase,
        isAdminRoute: false,
        match: ['materials'],
        id_element: 'sidebar-materials',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
    {
        name: '2HQ',
        link: `/dashboard/2hq`,
        icon: seoIcon,
        isAdminRoute: false,
        match: ['seo', '2hq'],
        id_element: 'sidebar-seo',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
    },
    {
        name: 'Agent',
        link: `/dashboard/agent`,
        icon: agentIcon,
        isAdminRoute: false,
        match: ['agent'],
        id_element: 'sidebar-agent',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        pro: false,
    },
    {
        name: 'Library',
        link: `/dashboard/media`,
        icon: databaseIcon,
        isAdminRoute: false,
        match: ['media', 'content-sheets', 'datasets', 'dataset', 'templates', 'prompts', 'flows'],
        id_element: 'sidebar-library',
        environments: ['development', 'staging', 'production'],
        group: 'workspace',
        subItems: [
            { name: 'Media', link: '/dashboard/media' },
            { name: 'Content Sheets', link: '/dashboard/content-sheets' },
            { name: 'Datasets', link: '/dashboard/datasets' },
            { name: 'Structures', link: '/dashboard/templates' },
            { name: 'Prompts', link: '/dashboard/prompts' },
            { name: 'My Flows', link: '/dashboard/flows/my-workflows' },
            { name: 'Flow Results', link: '/dashboard/flows/results' },
            { name: 'Flow Store', link: '/dashboard/flows/store' },
        ],
        pro: false,
    },
    // ── INFORMATION ──────────────────────────────────────────────────────────
    {
        name: 'Identity Engine',
        link: `/dashboard/assets/companies`,
        icon: Building2,
        isAdminRoute: false,
        match: ['assets/companies', 'assets/products', 'assets/people', 'assets/identities', 'assets/brand-assets'],
        id_element: 'sidebar-identity',
        environments: ['development', 'staging', 'production'],
        group: 'information',
        navigationVisibility: 'parked' as NavigationVisibility,
        subItems: [
            { name: 'Identities', link: '/dashboard/assets/identities' },
            { name: 'Companies', link: '/dashboard/assets/companies' },
            { name: 'Products', link: '/dashboard/assets/products' },
            { name: 'People', link: '/dashboard/assets/people' },
        ],
        pro: false,
    },
    {
        name: 'Visual Engine',
        link: `/dashboard/visual-engine`,
        icon: Image,
        isAdminRoute: false,
        match: ['visual-engine', 'persuasion-lab/creative-context', 'tools'],
        id_element: 'sidebar-visual-engine',
        environments: ['development', 'staging', 'production'],
        group: 'information',
        subItems: [
            {
                name: 'Visual Profiles',
                link: '/dashboard/visual-engine',
                activeWhenPathIncludes: '/visual-engine',
                activeWhenPathExcludes: '/visual-engine/api-settings',
            },
            {
                name: 'API Settings',
                link: '/dashboard/visual-engine/api-settings',
                activeWhenPathIncludes: '/visual-engine/api-settings',
            },
            {
                name: 'Story Board Profiles',
                link: '/dashboard/persuasion-lab/creative-context',
                activeWhenPathIncludes: 'persuasion-lab/creative-context',
            },
            { name: 'Brand Assets', link: '/dashboard/tools/assets' },
        ],
        pro: false,
    },
    {
        name: 'Strategy Assets',
        link: `/dashboard/assets/goals`,
        icon: Briefcase,
        isAdminRoute: false,
        match: ['assets/targets', 'assets/goals', 'assets/claims', 'assets/tfds', 'assets/evidence'],
        id_element: 'sidebar-strategic',
        environments: ['development', 'staging', 'production'],
        group: 'information',
        subItems: [
            { name: 'Goals', link: '/dashboard/assets/goals' },
            { name: 'Targets', link: '/dashboard/assets/targets' },
            { name: 'Claims', link: '/dashboard/assets/claims' },
            { name: 'Mindset Frameworks', link: '/dashboard/assets/tfds' },
            { name: 'Evidence', link: '/dashboard/assets/evidence' },
        ],
        pro: false,
    },

    // ── STRATEGY ──────────────────────────────────────────────────────────────
    {
        name: 'Strategy Engine',
        link: `/dashboard/brand-strategy/foundations`,
        icon: BookOpen,
        isAdminRoute: false,
        match: ['brand-strategy/foundations'],
        id_element: 'sidebar-brand-baseline',
        environments: ['development', 'staging', 'production'],
        group: 'strategy',
        navigationVisibility: 'parked' as NavigationVisibility,
        pro: false,
        defaultExpanded: true,
        subItems: [{ name: 'Foundation Profiles', link: '/dashboard/brand-strategy/foundations' }],
    },
    {
        name: 'Interview',
        link: `/dashboard/interview`,
        icon: Mic,
        isAdminRoute: false,
        match: ['interview', 'knowledge-review', 'assets/heft', 'events'],
        id_element: 'sidebar-interview-coach',
        environments: ['development', 'staging', 'production'],
        group: 'strategy',
        navigationVisibility: 'parked' as NavigationVisibility,
        pro: false,
        defaultExpanded: true,
        subItems: [
            { name: 'Dashboard', link: '/dashboard/interview', exactMatch: true },
            { name: 'Knowledge Review', link: '/dashboard/knowledge-review' },
            { name: 'HEFT Profile', link: '/dashboard/assets/heft' },
            { name: 'Events', link: '/dashboard/events' },
        ],
    },
    {
        name: 'Situations',
        link: `/dashboard/persuasion-lab/argument-builder`,
        icon: Sparkles,
        isAdminRoute: false,
        match: ['persuasion-lab/argument-builder', 'persuasion-lab/stories'],
        id_element: 'sidebar-situations',
        environments: ['development', 'staging', 'production'],
        group: 'strategy',
        pro: false,
        subItems: [
            { name: 'Situations', link: '/dashboard/persuasion-lab/argument-builder' },
            {
                name: 'Story Design',
                link: '/dashboard/persuasion-lab/stories',
                activeWhenPathIncludes: '/stories',
                activeWhenPathExcludes: '/argument-builder',
            },
        ],
    },
    {
        name: 'Trust Portraits',
        link: `/dashboard/portraits`,
        icon: Map,
        isAdminRoute: false,
        match: ['portraits/people', 'portraits/companies', 'portraits/products', 'portraits'],
        id_element: 'sidebar-portraits',
        environments: ['development', 'staging', 'production'],
        group: 'construct',
        pro: false,
    },

    // ── CONSTRUCT ─────────────────────────────────────────────────────────────
    {
        name: 'Campaigns',
        link: `/dashboard/campaigns`,
        icon: Target,
        isAdminRoute: false,
        match: ['campaigns', 'messaging-campaign', 'influencer-brief'],
        id_element: 'sidebar-campaigns',
        environments: ['development', 'staging', 'production'],
        group: 'construct',
        subItems: [
            { name: 'Dashboard', link: '/dashboard/campaigns', exactMatch: true },
            {
                name: 'Influencer Brief',
                link: '/dashboard/influencer-brief/start',
                activeWhenPathIncludes: '/influencer-brief',
            },
        ],
        pro: false,
    },
    {
        name: 'Rhetorical Workbench',
        link: `/dashboard/campaigns/rhetorical-workbench`,
        icon: BookOpen,
        isAdminRoute: false,
        match: ['campaigns/rhetorical-workbench'],
        id_element: 'sidebar-rhetorical-workbench',
        environments: ['development', 'staging', 'production'],
        group: 'construct',
        pro: false,
    },
    {
        name: 'Visual Studios',
        link: `/dashboard/campaigns/visual-studios/image-studio`,
        icon: Sparkles,
        isAdminRoute: false,
        match: ['campaigns/visual-studios'],
        id_element: 'sidebar-visual-studios',
        environments: ['development', 'staging', 'production'],
        group: 'construct',
        subItems: [
            {
                name: 'Image Studio',
                link: '/dashboard/campaigns/visual-studios/image-studio',
                activeWhenPathIncludes: '/campaigns/visual-studios/image-studio',
            },
            {
                name: 'Product Studio',
                link: '/dashboard/campaigns/visual-studios/product-studio',
                activeWhenPathIncludes: '/campaigns/visual-studios/product-studio',
            },
        ],
        pro: false,
    },
]

export const generalItems = [
    {
        name: 'Settings',
        link: `/dashboard/settings/personal`,
        icon: settingsIcon,
        isAdminRoute: false,
        match: ['settings'],
        id_element: 'sidebar-settings',
        environments: ['development', 'staging', 'production'],
        group: 'general',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
    {
        name: 'Support',
        link: `/dashboard/support`,
        icon: supportIcon,
        isAdminRoute: false,
        match: ['support'],
        id_element: 'sidebar-support',
        environments: ['development', 'staging', 'production'],
        group: 'general',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
    {
        name: 'Playbooks',
        link: `/dashboard/playbooks`,
        icon: Map,
        isAdminRoute: false,
        match: ['playbooks'],
        id_element: 'sidebar-playbooks',
        environments: ['development', 'staging', 'production'],
        group: 'general',
        pro: false,
        navigationVisibility: 'role' as NavigationVisibility,
    },
    {
        name: 'Help',
        link: `/dashboard/help`,
        icon: helpIcon,
        isAdminRoute: false,
        match: ['help'],
        id_element: 'sidebar-help',
        environments: ['development', 'staging', 'production'],
        group: 'general',
        pro: false,
        navigationVisibility: 'core' as NavigationVisibility,
    },
]
